import { selectEnterprise } from 'base/core/selectors';
import { IRestApiResponse } from 'interfaces';
import {
  all, call, put, select, takeLatest,
} from 'redux-saga/effects';
import { request } from 'utils/request';
import { FileType } from 'utils/file-uploader';
import { CoreSliceActions } from 'base/core/slice';
import { API } from './constants';
import {
  ExpertContent,
  FetchFolderSelectorMediaPayload,
  IMediaDelete,
  IMediaRequestDto,
  IMediaUpdatePayload,
  IMoveMedia,
  MediaLibraryState,
  MediaUploadPayload,
  SaveDownloadVideoTrimmed,
  Sort,
} from './interface';
import {
  selectMediaState, selectSaveAndShareRecording, selectSelectedFiles,
  selectSelectedFilesForUndo,
  selectSelectedTargetFolder,
} from './selector';
import { mediaLibraryAction } from './slice';

export function* fetchMediaLibrary(): any {
  try {
    const mediaState: MediaLibraryState = yield select(selectMediaState);
    const enterprise = yield select(selectEnterprise);

    const data: IMediaRequestDto = {
      page: mediaState.page + 1,
      limit: mediaState.perPage,
      path: mediaState.path,
      keyword: mediaState.filters.keyword,
      type: mediaState.filters.type,
    };

    if (mediaState.filters.keyword) {
      data.keyword = mediaState.filters.keyword;
    }

    if (mediaState.filters.type.includes(Sort.UPLOADED)) {
      data.ownership = Sort.UPLOADED;
    }

    if (mediaState.filters.type.includes(Sort.PURCHASED)) {
      data.ownership = Sort.PURCHASED;
    }
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_FETCH_MEDIA.path}/${enterprise.id}?keyword=${data.keyword}&path=${data.path}&type=${data.type}&page=${data.page}&limit=${data.limit}&sortBy=${mediaState.filters.sortBy}`,
        method: API.GET_FETCH_MEDIA.method,
      },
      {},
      true,
      false,
    );
    if (response.statusCode === 200) {
      yield put(mediaLibraryAction.setCount(response.data.total));
      yield put(mediaLibraryAction.deselectAllFiles());
      yield put(mediaLibraryAction.fetchMediaSucceed(response.data));
    } else {
      yield put(mediaLibraryAction.fetchMediaFailed());
    }
  } catch (error) {
    yield put(mediaLibraryAction.fetchMediaFailed());
  }
}

export function* fetchMediaWithSubFolders(): any {
  const mediaState: MediaLibraryState = yield select(selectMediaState);
  const enterprise = yield select(selectEnterprise);
  const data: IMediaRequestDto = {
    page: mediaState.page + 1,
    limit: 10,
    path: mediaState.path,
    keyword: mediaState.filters.keyword,
    type: mediaState.filters.type,
  };
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_FETCH_MEDIA.path}/${enterprise.id}?keyword=${data.keyword}&path=${'/root'}&type=${FileType.FOLDER}&sortBy=${mediaState.filters.sortBy}&withSubFolders=true`,
        method: API.GET_FETCH_MEDIA.method,
      },
      {},
      true,
    );
    if (response.statusCode === 200) {
      yield put(mediaLibraryAction.fetchMediaWithSubFoldersSucceed(response.data.result));
    } else {
      yield put(mediaLibraryAction.fetchMediaWithSubFoldersFailed());
    }
  } catch (error) {
    yield put(mediaLibraryAction.fetchMediaWithSubFoldersFailed());
  }
}

export function* fetchRemainingStroage(): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_REMAINING_STROAGE.path}`,
        method: API.GET_REMAINING_STROAGE.method,
      },
      {},
      true,
      false,
    );
    if (response.statusCode === 200) {
      yield put(mediaLibraryAction.fetchRemainingStroageSucceed(response.data));
    } else {
      yield put(mediaLibraryAction.fetchRemainingStroageFailed());
    }
  } catch (error) {
    yield put(mediaLibraryAction.fetchRemainingStroageFailed());
  }
}

export function* fetchFolderSelectorMedia({ payload }: FetchFolderSelectorMediaPayload): any {
  try {
    const enterprise = yield select(selectEnterprise);
    const mediaState: MediaLibraryState = yield select(selectMediaState);
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_FETCH_MEDIA.path}/${enterprise.id}?keyword=${''}&path=${payload?.folderPath ?? '/root'}&sortBy=${mediaState.filters.sortBy}&indicateSubFolders=${payload?.folderPath ? 'false' : 'true'}&recursive=${payload?.folderPath ? 'true' : 'false'}&limit=0`,

        method: API.GET_FETCH_MEDIA.method,
      },
      {},
      true,
      false,
    );
    if (response.statusCode === 200) {
      yield put(mediaLibraryAction.fetchFolderSelectorMediaSucceed(
        {
          result: response.data.result,
          parentFolderId: payload?.parentFolderId ?? '',
        },
      ));
    } else {
      yield put(mediaLibraryAction.fetchFolderSelectorMediaFailed());
    }
  } catch (error) {
    yield put(mediaLibraryAction.fetchFolderSelectorMediaFailed());
  }
}

export function* createMediaContent({ payload }: MediaUploadPayload): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.POST_UPLOAD_MEDIA.path}`,
        method: API.POST_UPLOAD_MEDIA.method,
      },
      { ...payload },
      true,
      false,
    );
    if (response.statusCode === 201) {
      yield put(mediaLibraryAction.uploadMediaSucceed(response.data));
      yield payload.resolve();
      yield put(mediaLibraryAction.fetchMedia());
    } else {
      yield put(mediaLibraryAction.uploadMediaFailed());
      yield payload.reject();
    }
  } catch (error) {
    yield put(mediaLibraryAction.uploadMediaFailed());
    yield payload.reject();
  }
}

export function* deleteSelectedMedia(): any {
  try {
    const mediaState = yield select(selectMediaState);

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < mediaState.selectedFiles.length; i++) {
      const file = mediaState.media.find(
        (f: ExpertContent) => f._id === mediaState.selectedFiles[i],
      )!;

      const data = {
        id: file!._id,
        contentUrl: file.url ? file.url : null,
        fileSize: file.fileSize,
      };

      yield call(
        request,
        {
          path: `${API.DELETE_DELETE_MEDIA.path}`,
          method: API.DELETE_DELETE_MEDIA.method,
        },
        data,
        true,
        false,
      );
    }

    yield put(mediaLibraryAction.deleteSelectedMediaSucceed());
    yield put(mediaLibraryAction.deselectAllFiles());
    yield put(mediaLibraryAction.deselectFilesSelectedForUndo());
    yield put(mediaLibraryAction.fetchMedia());
  } catch (error) {
    yield put(mediaLibraryAction.deleteSelectedMediaFailed());
  }
}

function* deleteMedia({ payload }: IMediaDelete): any {
  const mediaState = yield select(selectMediaState);

  const data = {
    id: payload.id,
    contentUrl: payload.contentUrl,
    fileSize: payload.fileSize,
  };

  try {
    const isMediaSelected = mediaState.selectedFiles.includes(payload.id);

    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.DELETE_DELETE_MEDIA.path}`,
        method: API.DELETE_DELETE_MEDIA.method,
      },
      data,
      true,
      false,
    );

    if (response.statusCode === 201) {
      yield put(mediaLibraryAction.deleteMediaSucceed(response.data));
    } else {
      yield put(mediaLibraryAction.deleteMediaFailed());
    }

    if (isMediaSelected) {
      yield put(mediaLibraryAction.toggleSelectedFile(payload.id));
    }

    yield put(mediaLibraryAction.fetchMedia());
  } catch (error) {
    yield put(mediaLibraryAction.deleteMediaFailed());
  }
}

function* updateMedia({ payload }: IMediaUpdatePayload): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.POST_UPDATE_MEDIA.path}`,
        method: API.POST_UPDATE_MEDIA.method,
      },
      payload,
      true,
      false,
    );
    if (response.statusCode === 201) {
      yield put(mediaLibraryAction.updateMediaSucceed(response.data));
      yield put(mediaLibraryAction.deselectAllFiles());
      yield put(mediaLibraryAction.deselectFilesSelectedForUndo());
      yield put(CoreSliceActions.setMessage(
        {
          message: 'Your file has been updated succefully!',
          success: true,
        },
      ));
      yield put(mediaLibraryAction.fetchMedia());
      yield put(mediaLibraryAction.setRenameMediaFormOpen(false));
    } else {
      yield put(mediaLibraryAction.updateMediaFailed());
      yield put(CoreSliceActions.setMessage(
        {
          message: response?.message || 'Media update failed !',
          success: false,
        },
      ));
    }
  } catch (error:any) {
    yield put(mediaLibraryAction.updateMediaFailed());
  }
}
export function* moveSelectedMedia({ payload }: IMoveMedia): any {
  const selectedFiles = yield select(selectSelectedFiles);
  const seletedTargetFolder = yield select(selectSelectedTargetFolder);

  const data = {
    mediaItems: selectedFiles,
    targetFolder: seletedTargetFolder,
  };

  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.POST_MOVE_MEDIA.path}`,
        method: API.POST_MOVE_MEDIA.method,
      },
      data,
      true,
      false,
    );
    if (response.statusCode === 201) {
      yield put(mediaLibraryAction.moveMediaSucceed());
      yield put(mediaLibraryAction.setSelectedTargetFolder(null));
      yield put(mediaLibraryAction.deselectAllFiles());
      yield put(mediaLibraryAction.fetchFolderSelectorMedia());
      yield put(mediaLibraryAction.fetchMedia());
      if (!payload?.isDragged) {
        yield put(mediaLibraryAction.selectUndoButtonDisabled(false));
      }
      yield put(CoreSliceActions.setMessage(
        {
          message: 'Your media has been moved succefully!',
          success: true,
        },
      ));
    } else {
      yield put(mediaLibraryAction.moveMediaFailed());
      yield put(CoreSliceActions.setMessage(
        {
          message: response?.message || 'Media move failed !',
          success: false,
        },
      ));
    }
  } catch (error) {
    yield put(mediaLibraryAction.moveMediaFailed());
    console.error(error);
  }
}

export function* undoMediaMove(): any {
  const undoItems = yield select(selectSelectedFilesForUndo);
  const data = {
    undoItems,
  };

  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.POST_MOVE_MEDIA.path}?isUndo=true`,
        method: API.POST_MOVE_MEDIA.method,
      },
      data,
      true,
      false,
    );
    if (response.statusCode === 201) {
      yield put(mediaLibraryAction.undoMediaMoveSucceed());
      yield put(mediaLibraryAction.fetchMedia());
      yield put(mediaLibraryAction.selectUndoButtonDisabled(true));
      yield put(mediaLibraryAction.clearSelectedFilesForUndo());
      yield put(mediaLibraryAction.setOpenFolderSelect(false));

      yield put(CoreSliceActions.setMessage(
        {
          message: 'Your media has been moved succefully!',
          success: true,
        },
      ));
    } else {
      yield put(mediaLibraryAction.undoMediaMoveFailed());
      yield put(CoreSliceActions.setMessage(
        {
          message: response?.message || 'Undo media move failed !',
          success: false,
        },
      ));
    }
  } catch (error) {
    yield put(mediaLibraryAction.undoMediaMoveFailed());
    console.error(error);
  }
}

export function* saveDownloadTrimmedVideoGenerator({ payload }: SaveDownloadVideoTrimmed) {
  try {
    const response: IRestApiResponse = yield call(
      request,
      { path: `${API.POST_VIDEO_TRIM.path}`, method: API.POST_VIDEO_TRIM.method },
      payload,
    );

    if (response.statusCode === 201) {
      yield put(mediaLibraryAction.saveDownloadTrimmedVideoSuccess());
      yield payload.resolve();
    } else {
      yield put(mediaLibraryAction.saveDownloadTrimmedVideoFailed());
      yield payload.reject();
    }
  } catch (error) {
    yield put(mediaLibraryAction.saveDownloadTrimmedVideoFailed());
    yield payload.reject();
  }
}

export function* setDownloadMedia(): any {
  yield put(mediaLibraryAction.setOpenFolderSelect(true));
}

export function* handleSaveAndShareRecording(): any {
  const SelectShareRecording = yield select(selectSaveAndShareRecording);

  if (SelectShareRecording !== null) {
    yield put(mediaLibraryAction.setOpenFolderSelect(true));
  }
}

export function* mediaSaga() {
  yield all([
    takeLatest(mediaLibraryAction.setPage.type, fetchMediaLibrary),
    takeLatest(mediaLibraryAction.setPerPage.type, fetchMediaLibrary),
    takeLatest(mediaLibraryAction.setKeyword.type, fetchMediaLibrary),
    takeLatest(mediaLibraryAction.setType.type, fetchMediaLibrary),
    takeLatest(mediaLibraryAction.setSortBy.type, fetchMediaLibrary),
    takeLatest(mediaLibraryAction.clearFilters.type, fetchMediaLibrary),
    takeLatest(mediaLibraryAction.setPath.type, fetchMediaLibrary),
    takeLatest(mediaLibraryAction.fetchMedia.type, fetchMediaLibrary),
    takeLatest(mediaLibraryAction.fetchRemainingStroage.type, fetchRemainingStroage),
    takeLatest(mediaLibraryAction.uploadMedia.type, createMediaContent),
    takeLatest(mediaLibraryAction.deleteSelectedMedia.type, deleteSelectedMedia),
    takeLatest(mediaLibraryAction.deleteMedia.type, deleteMedia),
    takeLatest(mediaLibraryAction.updateMedia.type, updateMedia),
    takeLatest(mediaLibraryAction.saveDownloadTrimmedVideo.type, saveDownloadTrimmedVideoGenerator),
    takeLatest(mediaLibraryAction.fetchMediaWithSubFolders.type, fetchMediaWithSubFolders),
    takeLatest(mediaLibraryAction.moveMedia.type, moveSelectedMedia),
    takeLatest(mediaLibraryAction.undoMediaMove.type, undoMediaMove),
    takeLatest(mediaLibraryAction.setisDownloadMediaEnabled.type, setDownloadMedia),
    takeLatest(mediaLibraryAction.setSaveAndShareRecording.type, handleSaveAndShareRecording),
    takeLatest(mediaLibraryAction.fetchFolderSelectorMedia.type, fetchFolderSelectorMedia),
  ]);
}

export default mediaSaga;
